"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Box,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { importPassagensErp, getImportPassagensErpJob, cancelImportPassagensErpJob } from "../../api/client";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";

const POLL_MS = 1600;

function extractPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data !== undefined && raw.data !== null && typeof raw.data === "object") return raw.data;
  return raw;
}

function formatApiError(e, fallback) {
  const u = getUserFacingApiMessage(e);
  const action =
    u.action ||
    (e && typeof e === "object" && typeof e.userFacingAction === "string" && e.userFacingAction.trim()) ||
    (e && typeof e === "object" && typeof e.userAction === "string" && e.userAction.trim()) ||
    "";
  const raw = String(u.message || e?.message || e?.code || "");
  const isTimeout =
    e?.code === "ECONNABORTED" ||
    /timeout of \d+ms exceeded/i.test(raw) ||
    /timed?\s*out/i.test(raw);
  const base = isTimeout ? null : u.message || fallback;
  return { message: base, action: action || null, isTimeout };
}

function collectLogLines({ job, syncResult, phase, error, errorAction }) {
  const lines = [];
  if (Array.isArray(job?.log) && job.log.length) {
    lines.push(...job.log.map((l) => String(l)));
  }
  if (syncResult?.message) lines.push(String(syncResult.message));
  if (syncResult?.userAction) lines.push(String(syncResult.userAction));
  if (Array.isArray(syncResult?.warnings)) {
    for (const w of syncResult.warnings) {
      const s = String(w || "").trim();
      if (s) lines.push(s);
    }
  }
  if (Array.isArray(job?.result?.warnings)) {
    for (const w of job.result.warnings) {
      const s = String(w || "").trim();
      if (s) lines.push(s);
    }
  }
  if (error) lines.push(String(error));
  if (errorAction) lines.push(String(errorAction));
  if (!lines.length && phase === "starting") return ["…"];
  return lines;
}

export default function ImportPassagensErpDialog({ open, onClose, projectId, onCompleted }) {
  const { t } = useTranslation("products");
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [errorAction, setErrorAction] = useState("");
  const [job, setJob] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const pollRef = useRef(null);
  const runIdRef = useRef(0);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      clearPoll();
      return undefined;
    }

    const runId = ++runIdRef.current;

    setPhase("idle");
    setJob(null);
    setSyncResult(null);
    setError("");
    setErrorAction("");

    if (!projectId) {
      setPhase("failed");
      setError(t("toolbar.importPassagensErpNeedProject"));
      setErrorAction(t("importPassagensDialog.needProjectAction"));
      return undefined;
    }

    const pollOnce = async (jobId) => {
      try {
        const raw = await getImportPassagensErpJob(jobId);
        if (runId !== runIdRef.current) return;
        const j = extractPayload(raw) || raw;
        setJob(j);
        const st = String(j?.status || "");
        if (st === "completed" || st === "failed" || st === "cancelled") {
          clearPoll();
          setPhase(st);
          if (st === "failed") {
            setError(j?.errorMessage || j?.result?.message || t("importPassagensDialog.errorGeneric"));
            setErrorAction(j?.userAction || j?.result?.userAction || t("importPassagensDialog.errorActionFallback"));
          }
          if (st === "completed" && onCompletedRef.current) {
            await onCompletedRef.current();
          }
        }
      } catch (e) {
        if (runId !== runIdRef.current) return;
        const { message, action, isTimeout } = formatApiError(e, t("importPassagensDialog.errorGeneric"));
        setError(isTimeout ? t("importPassagensDialog.errorTimeout") : message);
        setErrorAction(action || t("importPassagensDialog.errorActionFallback"));
        clearPoll();
        setPhase("failed");
      }
    };

    const run = async () => {
      setPhase("starting");
      try {
        const raw = await importPassagensErp({ id_projeto: Number(projectId) });
        if (runId !== runIdRef.current) return;
        const payload = extractPayload(raw) || raw;

        if (payload?.accepted && payload.jobId) {
          setPhase("running");
          await pollOnce(payload.jobId);
          if (runId !== runIdRef.current) return;
          pollRef.current = setInterval(() => pollOnce(payload.jobId), POLL_MS);
        } else if (payload && payload.ok === false) {
          setSyncResult(payload);
          setPhase("failed");
          setError(payload.message || t("importPassagensDialog.errorGeneric"));
          setErrorAction(payload.userAction || t("importPassagensDialog.errorActionFallback"));
        } else {
          setSyncResult(payload);
          setPhase("completed");
          if (onCompletedRef.current) await onCompletedRef.current();
        }
      } catch (e) {
        if (runId !== runIdRef.current) return;
        const { message, action, isTimeout } = formatApiError(e, t("importPassagensDialog.errorGeneric"));
        setError(isTimeout ? t("importPassagensDialog.errorTimeout") : message);
        setErrorAction(action || t("importPassagensDialog.errorActionFallback"));
        setPhase("failed");
      }
    };

    run();

    return () => {
      clearPoll();
    };
  }, [open, projectId, t]);

  const handleClose = () => {
    clearPoll();
    onClose?.();
  };

  const progressValue =
    job && job.overallProgress != null && Number.isFinite(Number(job.overallProgress))
      ? Math.min(100, Math.round(Number(job.overallProgress) * 100))
      : job && job.partnerProgress != null && Number.isFinite(Number(job.partnerProgress))
        ? Math.min(100, Math.round(Number(job.partnerProgress) * 100))
        : null;

  const createdAtMs = (() => {
    const s = job?.createdAt || job?.updatedAt || null;
    const tMs = s ? Date.parse(String(s)) : NaN;
    return Number.isFinite(tMs) ? tMs : null;
  })();
  const queuedForSec =
    String(job?.status || "") === "queued" && createdAtMs ? Math.floor((Date.now() - createdAtMs) / 1000) : null;
  const queuedLooksStuck = queuedForSec != null && queuedForSec >= 90;

  const canCancel =
    job &&
    (String(job.status) === "queued" || String(job.status) === "running" || String(job.status) === "cancel_requested") &&
    !job.cancelRequested;

  const handleCancel = async () => {
    const jid = String(job?.jobId || "").trim();
    if (!jid) return;
    try {
      await cancelImportPassagensErpJob(jid);
      setJob((prev) =>
        prev ? { ...prev, cancelRequested: true, status: prev.status === "queued" ? "cancel_requested" : prev.status } : prev
      );
    } catch (e) {
      const { message } = formatApiError(e, t("importPassagensDialog.cancelFailed"));
      setError(message);
    }
  };

  const showSyncNote = Boolean(syncResult) && syncResult.accepted === false;
  const resultStats = syncResult || job?.result || null;
  const isEmptySuccess =
    phase === "completed" &&
    resultStats &&
    Number(resultStats.trechosProcessed || 0) === 0 &&
    Number(resultStats.createdProducts || 0) === 0;
  const logLines = collectLogLines({ job, syncResult, phase, error: null, errorAction: null });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={String(job?.status) === "running" || phase === "starting"}
    >
      <DialogTitle>{t("importPassagensDialog.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("importPassagensDialog.subtitle")}
        </Typography>

        {showSyncNote ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("importPassagensDialog.syncNote")}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              {error}
            </Typography>
            {errorAction ? (
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                {errorAction}
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        {phase === "starting" ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {String(job?.status) === "queued" ||
        String(job?.status) === "running" ||
        String(job?.status) === "cancel_requested" ||
        phase === "starting" ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {String(job?.status || "") === "queued"
                ? t("importPassagensDialog.phaseQueued")
                : String(job?.status || "") === "cancel_requested"
                  ? t("importPassagensDialog.phaseCancelRequested")
                  : t("importPassagensDialog.phaseRunning")}
              {job?.partner ? ` · ${t("importPassagensDialog.partnerLine", { partner: job.partner })}` : ""}
              {queuedForSec != null ? ` · ${t("importPassagensDialog.queuedFor", { sec: queuedForSec })}` : ""}
            </Typography>
            {progressValue != null ? (
              <LinearProgress variant="determinate" value={progressValue} />
            ) : (
              <LinearProgress />
            )}
            {queuedLooksStuck ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("importPassagensDialog.queuedStuckHint")}
              </Alert>
            ) : null}
            {job?.trechosProcessed != null ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {t("importPassagensDialog.statsLine", {
                  trechos: job.trechosProcessed ?? 0,
                  created: job.createdProducts ?? 0,
                  skipped: job.skippedProducts ?? 0,
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {phase === "completed" && resultStats ? (
          <Alert severity={isEmptySuccess ? "warning" : "success"} sx={{ mb: 2 }}>
            {isEmptySuccess
              ? t("importPassagensDialog.emptyResult")
              : t("toolbar.importPassagensErpSuccessDetail", {
                  trechos: resultStats.trechosProcessed ?? 0,
                  created: resultStats.createdProducts ?? 0,
                  skipped: resultStats.skippedProducts ?? 0,
                })}
          </Alert>
        ) : null}

        {phase === "failed" && !error && job?.errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {job.errorMessage}
            {job.userAction || job.result?.userAction ? (
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                {job.userAction || job.result?.userAction}
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("importPassagensDialog.progressLog")}
        </Typography>
        <List dense sx={{ maxHeight: 280, overflow: "auto", bgcolor: "action.hover", borderRadius: 1 }}>
          {logLines.map((line, i) => (
            <ListItem key={i} disablePadding sx={{ px: 1, py: 0.25 }}>
              <ListItemText primary={line} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        {canCancel ? (
          <Button color="error" onClick={handleCancel}>
            {t("importPassagensDialog.cancel")}
          </Button>
        ) : null}
        <Button onClick={handleClose}>{t("importPassagensDialog.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
